import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const basicFetchCode = `// The Modern Standard: Fetch API
async function getUserData(userId) {
    try {
        // Fetch returns a Promise that resolves to a Response object
        const response = await fetch(\`https://api.example.com/users/\${userId}\`);
        
        // CRITICAL: Fetch ONLY rejects on network errors (DNS, Timeout).
        // It does NOT reject on HTTP 404 or 500 errors.
        if (!response.ok) {
            throw new Error(\`Network response was not ok: \${response.status}\`);
        }

        const data = await response.json();
        console.log("Structured Data:", data);
        return data;
    } catch (error) {
        console.error("Transmission Error:", error.message);
    }
}

getUserData(1);`;

const postRequestCode = `// Transactional Integrity: POST Requests
async function syndicateArticle(article) {
    const endpoint = 'https://api.filefusion.com/articles';
    
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer FF_SYSTEM_TOKEN_992'
        },
        body: JSON.stringify(article)
    });

    if (response.status === 201) {
        console.log("Resource Created Successfully");
    }
    
    return await response.json();
}`;

const headersCode = `// Metadata Management: Custom Headers
const securityHeaders = new Headers({
    'X-System-Agent': 'FileFusion-Core',
    'X-Nonce-Token': crypto.randomUUID(),
    'Accept': 'application/vnd.api+json'
});

async function fetchSecure(url) {
    const response = await fetch(url, { headers: securityHeaders });
    
    // Inspecting Incoming Metadata
    console.log("Content-Type:", response.headers.get('content-type'));
    console.log("Server Runtime:", response.headers.get('server'));
    
    return response.ok;
}`;

const JSFetch = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Networking & Architecture</div>
                <h1>The Fetch API: Modern Networking Protocols</h1>
                <p className="hero-lead">
                    Master the high-performance interface for making HTTP requests in modern JavaScript.
                    Navigate the JS-Network Bridge, implement secure transaction protocols, and
                    engineer resilient data flows for production-scale applications.
                </p>
            </div>

            <div className="content-section">
                <h2>The JS-Network Bridge: How Fetch Works</h2>
                <p>
                    In the early days of the web, network requests were handled by the cumbersome `XMLHttpRequest`.
                    Modern engineering relies on the **Fetch API**, a promise-based interface that provides
                    a more powerful and flexible feature set. When you invoke `fetch()`, you aren't just
                    calling a function; you are initiating an asynchronous operation that crosses the boundary
                    between the JavaScript execution environment and the browser's internal network stack.
                </p>
                <p>
                    One of the most important architectural nuances of Fetch is its error-handling model.
                    Unlike many other libraries, a `fetch()` promise **only rejects on network failure**
                    (such as a DNS issue or a dropped connection). It does *not* reject on HTTP error
                    statuses like 404 (Not Found) or 500 (Internal Server Error). This design requires
                    software engineers to manually verify the `response.ok` property before attempting
                    to process the data.
                </p>
                <CodeBlock code={basicFetchCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>CORS: The Security Gatekeeper</h2>
                <p>
                    When making requests to a different domain, you encounter **Cross-Origin Resource Sharing (CORS)**.
                    This is a security mechanism enforced by the browser to prevent malicious sites from
                    reading sensitive data from another site. A production-grade engineer must understand
                    "Preflight" requestswhere the browser sends an `OPTIONS` request to the server
                    to ask for permission before sending the actual data.
                </p>
                <ProTip><p>
                        CORS preflights can add 100-300ms of latency to your requests. To minimize this,
                        keep your requests "Simple" (standard ports, safe methods like GET/POST,
                        and common headers) or configure your server to return an `Access-Control-Max-Age`
                        header to cache the preflight permission in the user's browser.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>Transactional Integrity: POST, PUT, and DELETE</h2>
                <p>
                    Networking is more than just fetching data; it's about managing state across the wire.
                    Using the correct HTTP methods is essential for **Semantic Clarity** and server-side
                    routing efficiency. `POST` is used for creating resources, `PUT` for complete
                    replacement, and `PATCH` for surgical, partial updates.
                </p>
                <p>
                    When sending data, you must explicitly set the `Content-Type` header to inform the
                    server how to parse the incoming binary stream. Most modern APIs expect `application/json`,
                    which requires you to serialize your JavaScript objects using `JSON.stringify()`.
                </p>
                <CodeBlock code={postRequestCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Metadata Management: Headers and Response Objects</h2>
                <p>
                    Headers are the "Envelope" that carries your data. They contain vital metadata about
                    authentication, caching policies, and content negotiation. The `Headers` object in
                    JavaScript allows for granular manipulation of these signals.
                </p>
                <p>
                    Similarly, the `Response` object is not just a container for the body; its a
                    comprehensive view of the transmission. It includes status codes, headers, and
                    the final URL after any redirects. Mastering the consumption of this metadata
                    is what separates basic scripters from systems engineers.
                </p>
                <CodeBlock code={headersCode} language="javascript" />
            </div>

            <KeyFacts><ul>
                    <li>**Verification:** Always check `response.ok` before parsing the body.</li>
                    <li>**Efficiency:** Use GET for idempotent reads to leverage browser caching.</li>
                    <li>**Security:** Understand CORS preflight triggers to avoid latency traps.</li>
                    <li>**Clarity:** Match HTTP methods (POST/PUT/PATCH) to logical operations.</li>
                    <li>**Metadata:** Use custom headers for system-level signaling and tracing.</li>
                    <li>**Resilience:** Wrap fetch operations in robust try/catch blocks for network safety.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSFetch;


